import { type ReactNode, useEffect, useRef } from 'react';
import { type StatusKind, statusOf } from '../lib/status.js';
import { Icon, type IconName } from './Icon.js';

// Potongan UI kecil yang dipakai banyak layar — markup sama dengan helper v1 (pill, A.sec, A.tabs, kpi, growth, cat).

export function Pill({ kind, v }: { kind: StatusKind; v: unknown }) {
  const [l, c] = statusOf(kind, v);
  return <span className={`pill ${c}`}>{l}</span>;
}

/** Judul seksi bergaya "Kategori ........ Lihat semua". */
export function Sec({ title, link, linkLbl = 'Lihat semua' }: { title: ReactNode; link?: string; linkLbl?: string }) {
  return (
    <div className="sec-h">
      <h3>{title}</h3>
      {link ? (
        <a href={link} data-go={link}>
          {linkLbl}
        </a>
      ) : null}
    </div>
  );
}

/** Tab halaman: tiap tab mengubah ?tab= pada base. [kunci, label, hitungan?] */
export function Tabs({ tabs, cur, base }: { tabs: [string, string, number?][]; cur: string; base: string }) {
  // HP: baris tab digeser jari — tab aktif digeser ke tampilan agar tidak tersembunyi di luar layar.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = ref.current;
    const on = box?.querySelector<HTMLElement>(`.tab[data-tab="${CSS.escape(cur)}"]`);
    if (box && on && box.scrollWidth > box.clientWidth) {
      box.scrollLeft = on.offsetLeft - (box.clientWidth - on.offsetWidth) / 2 - box.offsetLeft;
    }
  }, [cur]);
  return (
    <div className="tabs" ref={ref}>
      {tabs.map(([k, l, n]) => (
        <button key={k} type="button" className={`tab ${k === cur ? 'active' : ''}`} data-tab={k} data-go={`${base}?tab=${k}`}>
          {l}
          {n ? <span className="n">{n}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Kpi({
  lbl,
  val,
  sub = '',
  extra = '',
  go,
}: {
  lbl: ReactNode;
  val: ReactNode;
  sub?: ReactNode;
  extra?: string;
  go?: string;
}) {
  return (
    <div className={`kpi${go ? ' click' : ''} ${extra}`} data-go={go}>
      <span className="lbl">{lbl}</span>
      <span className="val">{val}</span>
      {sub ? <span className="sub">{sub}</span> : null}
    </div>
  );
}

export function Growth({ g, label = 'vs periode lalu' }: { g: number | null | undefined; label?: string }) {
  // Satu string per elemen — sama dengan v1 (lihat features/PORTING.md §1).
  if (g == null) return <span className="growth muted">{`— ${label}`}</span>;
  return (
    <span
      className={`growth ${g >= 0 ? 'up' : 'down'}`}
    >{`${g >= 0 ? '▲' : '▼'} ${Math.abs(g).toLocaleString('id-ID')}% ${label}`}</span>
  );
}

/** Kartu kategori bulat ("Menu cepat") — v1 cat() (toko.js & admin.js, markup sama). */
export function Cat({ go, ic, lbl, n = 0 }: { go: string; ic: IconName; lbl: string; n?: number }) {
  return (
    <button type="button" className="cat" data-go={go}>
      <span className="cat-ic">
        <Icon n={ic} />
        {n ? <i className="count">{n}</i> : null}
      </span>
      <span>{lbl}</span>
    </button>
  );
}

/** Kotak kosong dalam kartu (v1: '<div class="card"><div class="empty">…</div></div>'). */
export function EmptyCard({ children, cls = '' }: { children: ReactNode; cls?: string }) {
  return (
    <div className="card">
      <div className={`empty${cls ? ` ${cls}` : ''}`}>{children}</div>
    </div>
  );
}
